import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { QRCodeSVG } from "qrcode.react";
import { Table2, Plus, X, Trash2, Camera, Wrench, CircleDot, QrCode, Printer, Download } from "lucide-react";
import { toast } from "sonner";

const STATUS = {
  free: { label: "Trống", color: "#22ee9c", bg: "from-emerald-500/15 to-emerald-700/5", border: "border-emerald-400/40" },
  playing: { label: "Đang đấu", color: "#00e5ff", bg: "from-cyan-500/15 to-cyan-700/5", border: "border-cyan-400/40" },
  maintenance: { label: "Bảo trì", color: "#ffb020", bg: "from-yellow-500/15 to-yellow-700/5", border: "border-yellow-400/40" },
};

export default function AdminTables() {
  const [tables, setTables] = useState([]);
  const [creating, setCreating] = useState(false);
  const [editing, setEditing] = useState(null);
  const [qrTable, setQrTable] = useState(null);
  const [queue, setQueue] = useState([]);

  const load = () => {
    api.get("/tables").then(({ data }) => setTables(data));
    api.get("/queue").then(({ data }) => setQueue(data)).catch(() => setQueue([]));
  };
  useEffect(() => {
    load();
    const i = setInterval(load, 8000);
    return () => clearInterval(i);
  }, []);

  const queueCount = (tableId) => queue.filter((q) => q.table_id === tableId).length;

  const updateStatus = async (t, status) => {
    await api.patch(`/admin/tables/${t.id}`, { status });
    load();
  };

  const del = async (t) => {
    if (!window.confirm(`Xóa bàn ${t.number}?`)) return;
    await api.delete(`/admin/tables/${t.id}`);
    load();
  };

  return (
    <div className="p-6 lg:p-8">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="font-display font-bold text-3xl">Quản lý bàn bida</h1>
          <p className="text-white/40 text-sm mt-1">{tables.length} bàn</p>
        </div>
        <div className="flex gap-2">
          <Link to="/admin/qr-print" data-testid="qr-print-link" className="px-4 py-2.5 rounded-md text-sm border border-cyan-400/30 text-cyan-300 hover:bg-cyan-400/10 flex items-center gap-2">
            <Printer size={15} /> In QR tất cả
          </Link>
          <button onClick={() => setCreating(true)} data-testid="add-table-btn" className="px-4 py-2.5 rounded-md text-sm font-medium bg-gradient-to-r from-cyan-400 to-purple-500 text-black flex items-center gap-2">
            <Plus size={15} /> Thêm bàn
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {tables.map((t) => {
          const s = STATUS[t.status] || STATUS.free;
          return (
            <div key={t.id} className={`relative rounded-xl bg-gradient-to-br ${s.bg} border ${s.border} p-5 overflow-hidden`} data-testid={`table-card-${t.number}`}>
              <div className="flex items-start justify-between mb-4">
                <div>
                  <div className="text-[10px] uppercase tracking-widest text-white/40">Bàn số</div>
                  <div className="font-display font-black text-5xl" style={{ color: s.color }}>{t.number}</div>
                </div>
                <div className="flex flex-col items-end gap-1">
                  <CircleDot size={20} style={{ color: s.color }} className={t.status === "playing" ? "animate-pulse" : ""} />
                  {queueCount(t.id) > 0 && (
                    <span className="px-1.5 py-0.5 rounded text-[10px] font-display font-bold bg-cyan-400/20 text-cyan-300 border border-cyan-400/40" data-testid={`queue-badge-${t.number}`}>
                      {queueCount(t.id)} chờ
                    </span>
                  )}
                </div>
              </div>
              <div className="font-display font-semibold text-sm">{t.name || `Bàn ${t.number}`}</div>
              <div className="text-xs mt-1" style={{ color: s.color }}>{s.label}</div>

              {t.camera_url && (
                <a href={t.camera_url} target="_blank" rel="noreferrer" className="mt-3 inline-flex items-center gap-1.5 text-xs text-cyan-300 hover:underline">
                  <Camera size={12} /> Camera
                </a>
              )}

              <div className="mt-4 grid grid-cols-3 gap-1.5">
                {Object.entries(STATUS).map(([k, v]) => (
                  <button key={k} onClick={() => updateStatus(t, k)} data-testid={`set-${k}-${t.number}`}
                    className={`px-2 py-1.5 rounded text-[10px] font-medium uppercase tracking-wider transition ${t.status === k ? "bg-white/10" : "bg-white/[0.03] hover:bg-white/5 text-white/60"}`}>
                    {v.label}
                  </button>
                ))}
              </div>
              <div className="flex gap-2 mt-3">
                <button onClick={() => setQrTable(t)} data-testid={`qr-btn-${t.number}`} className="px-3 py-1.5 rounded-md text-xs border border-cyan-400/30 text-cyan-300 hover:bg-cyan-400/10 flex items-center gap-1.5">
                  <QrCode size={12} /> QR
                </button>
                <button onClick={() => setEditing(t)} className="flex-1 px-3 py-1.5 rounded-md text-xs border border-white/10 hover:bg-white/5">Sửa</button>
                <button onClick={() => del(t)} className="p-2 rounded-md text-red-400 hover:bg-red-500/10" data-testid={`delete-table-${t.number}`}><Trash2 size={14} /></button>
              </div>
            </div>
          );
        })}
      </div>

      {(creating || editing) && <TableModal table={editing} onClose={() => { setCreating(false); setEditing(null); }} onDone={() => { setCreating(false); setEditing(null); load(); }} />}
      {qrTable && <QRModal table={qrTable} onClose={() => setQrTable(null)} />}
    </div>
  );
}

const QRModal = ({ table, onClose }) => {
  const url = `${window.location.origin}/table/${table.number}`;
  const downloadSvg = () => {
    const svg = document.getElementById(`qr-svg-${table.number}`);
    if (!svg) return;
    const data = new XMLSerializer().serializeToString(svg);
    const blob = new Blob([data], { type: "image/svg+xml" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = `qr-ban-${table.number}.svg`;
    a.click();
  };
  return (
    <div className="fixed inset-0 z-50 grid place-items-center p-4 bg-black/80 backdrop-blur-sm" onClick={onClose} data-testid="qr-modal">
      <div className="w-full max-w-sm rounded-xl bg-[#0c0d14] border border-white/10 p-6" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-display font-semibold flex items-center gap-2"><QrCode size={16} className="text-cyan-300" /> QR Bàn {table.number}</h3>
          <button onClick={onClose} className="p-1 hover:bg-white/5 rounded"><X size={16} /></button>
        </div>
        <div className="bg-white p-4 rounded-lg flex items-center justify-center">
          <QRCodeSVG id={`qr-svg-${table.number}`} value={url} size={220} level="M" includeMargin={false} />
        </div>
        <div className="mt-3 text-xs text-white/50 break-all text-center">{url}</div>
        <div className="mt-4 flex gap-2">
          <button onClick={downloadSvg} className="flex-1 px-3 py-2 rounded-md text-sm border border-white/10 hover:bg-white/5 flex items-center justify-center gap-2" data-testid="qr-download">
            <Download size={14} /> Tải SVG
          </button>
          <a href={url} target="_blank" rel="noreferrer" className="flex-1 px-3 py-2 rounded-md text-sm border border-cyan-400/30 text-cyan-300 hover:bg-cyan-400/10 flex items-center justify-center gap-2">
            Mở thử →
          </a>
        </div>
        <div className="mt-3 text-[11px] text-white/40 text-center">Khách quét QR sẽ thấy trận đang đấu & link đăng ký leo rank</div>
      </div>
    </div>
  );
};

const TableModal = ({ table, onClose, onDone }) => {
  const [f, setF] = useState({
    number: table?.number || 1,
    name: table?.name || "",
    status: table?.status || "free",
    camera_url: table?.camera_url || "",
  });
  const submit = async (e) => {
    e.preventDefault();
    try {
      if (table) {
        await api.patch(`/admin/tables/${table.id}`, { name: f.name, status: f.status, camera_url: f.camera_url });
      } else {
        await api.post("/admin/tables", { ...f, number: Number(f.number) });
      }
      toast.success(table ? "Đã cập nhật" : "Đã thêm bàn");
      onDone();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Lỗi");
    }
  };
  return (
    <div className="fixed inset-0 z-50 grid place-items-center p-4 bg-black/80 backdrop-blur-sm" onClick={onClose}>
      <div className="w-full max-w-md rounded-xl bg-[#0c0d14] border border-white/10 p-6" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-display font-semibold">{table ? `Sửa bàn ${table.number}` : "Thêm bàn mới"}</h3>
          <button onClick={onClose} className="p-1 hover:bg-white/5 rounded"><X size={16} /></button>
        </div>
        <form onSubmit={submit} className="space-y-3" data-testid="table-form">
          {!table && (
            <label className="block">
              <span className="text-[10px] uppercase tracking-wider text-white/50">Số bàn *</span>
              <input type="number" required value={f.number} onChange={(e) => setF({ ...f, number: e.target.value })} className="mt-1 w-full bg-white/5 border border-white/10 rounded px-3 py-2 text-sm outline-none focus:border-cyan-400/60" />
            </label>
          )}
          <label className="block">
            <span className="text-[10px] uppercase tracking-wider text-white/50">Tên bàn</span>
            <input value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} className="mt-1 w-full bg-white/5 border border-white/10 rounded px-3 py-2 text-sm outline-none focus:border-cyan-400/60" />
          </label>
          <label className="block">
            <span className="text-[10px] uppercase tracking-wider text-white/50">Trạng thái</span>
            <select value={f.status} onChange={(e) => setF({ ...f, status: e.target.value })} className="mt-1 w-full bg-white/5 border border-white/10 rounded px-3 py-2 text-sm">
              <option value="free">Trống</option>
              <option value="playing">Đang đấu</option>
              <option value="maintenance">Bảo trì</option>
            </select>
          </label>
          <label className="block">
            <span className="text-[10px] uppercase tracking-wider text-white/50">Camera URL</span>
            <input value={f.camera_url} onChange={(e) => setF({ ...f, camera_url: e.target.value })} placeholder="rtsp://... hoặc http://..." className="mt-1 w-full bg-white/5 border border-white/10 rounded px-3 py-2 text-sm outline-none focus:border-cyan-400/60" />
          </label>
          <button type="submit" data-testid="submit-table" className="w-full py-2.5 rounded-md font-display font-semibold text-sm bg-gradient-to-r from-cyan-400 to-purple-500 text-black">{table ? "Lưu" : "Tạo bàn"}</button>
        </form>
      </div>
    </div>
  );
};
